import { useState } from 'react';

// components
import AnalyticsEventLogger from 'utils/AnalyticsEventLogger';
import {
  Cluster,
  LarAffectedResource,
  LarIgnoreReason,
  LARIgnoreStatus,
} from 'api/models';
import { SetStateFunction } from 'models/react';
import { useGetLARStatus } from 'api/frontend';
import useUserAccountState from 'hooks/useUserAccountState';
import CreateJiraTicketDialog from 'components/dialogs/CreateJiraDialog';
import MarkAsIgnoreDialog from './MarkAsIgnoreDialog';
import { Button, Tooltip } from 'subframe/index';
import * as SubframeCore from '@subframe/core';
import styles from '../LatestPageKbaHowToFixTab.module.scss';
import { markAsReasonDisplay } from '../models/markAs';
import { Stack } from 'components/utils';
import Iconify from 'components/Iconify';
import { ExtendedLAR } from '..';
import { Actions } from 'components/design-system';

interface ARStateProps {
  selectedLar: ExtendedLAR;
  cluster: Cluster;
  isDialogOpen?: boolean;
  setIsDialogOpen?: SetStateFunction<boolean>;
  affClustersMutate: SetStateFunction<boolean>;
  resources: LarAffectedResource[] | undefined;
}

export const ARState = ({
  selectedLar,
  cluster,
  affClustersMutate,
  resources,
}: ARStateProps) => {
  const { logEvent } = AnalyticsEventLogger();
  const { account } = useUserAccountState();

  const [isCreateJiraTicketDialogOpen, setIsCreateJiraTicketDialogOpen] =
    useState<boolean>(false);
  const [isIgnoreDialogOpen, setIsIgnoreDialogOpen] = useState<boolean>(false);
  const [selectedMarkAs, setSelectedMarkAs] = useState<LarIgnoreReason | null>(
    null,
  );

  const {
    data: dataLarStatus,
    isValidating: isValidatingLarStatus,
    isLoading: isLoadingLarStatus,
    mutate: mutateLarStatus,
  } = useGetLARStatus(cluster?.id, selectedLar.signature_id?.toLowerCase(), {
    request: { headers: { Authorization: `Bearer ${account.token}` } },
  });

  const getMarkedStatus =
    dataLarStatus?.ignore_status?.reason ||
    (dataLarStatus?.ignore ? false : undefined);

  const getJiraState = dataLarStatus?.jira_ticket
    ? {
        jiraId: dataLarStatus.jira_ticket?.split('/').pop() || '',
        jiraLink: dataLarStatus.jira_ticket,
      }
    : {};
  const getIgnoreStatus = dataLarStatus?.ignore_status?.status;

  const openJiraTicketLink = () => {
    window.open(getJiraState?.jiraLink);
    logEvent('navigate-jira-ticket', {
      clusterId: cluster.id,
      lar_id: selectedLar.signature_id,
    });
  };

  const createJiraTicket = () => {
    setIsCreateJiraTicketDialogOpen(!isCreateJiraTicketDialogOpen);
    logEvent('create-ticket-dialog', {
      clusterId: cluster.id,
      lar_id: selectedLar.signature_id,
    });
  };

  return (
    <div className={styles['actionButtons1']}>
      <CreateJiraTicketDialog
        lar={selectedLar}
        cluster={cluster}
        handleOpen={setIsCreateJiraTicketDialogOpen}
        isOpen={isCreateJiraTicketDialogOpen}
        mutateStatus={mutateLarStatus}
        resources={resources}
      />
      <MarkAsIgnoreDialog
        lar={selectedLar}
        cluster={cluster}
        handleOpen={setIsIgnoreDialogOpen}
        isOpen={isIgnoreDialogOpen}
        ignoreStatus={dataLarStatus?.ignore_status}
        mutateStatus={mutateLarStatus}
        mutateAffClusters={affClustersMutate}
        selectedMarkAs={selectedMarkAs || undefined}
      />

      <Actions
        key="actions"
        size="large"
        sections={[
          {
            title: 'Mark Risk',
            items: [
              <Actions.Item
                key="mark-as-false-positive"
                title="Mark as False Positive"
                icon="FeatherFileWarning"
                onClick={() => {
                  setIsIgnoreDialogOpen(true);
                  setSelectedMarkAs(LarIgnoreReason.FalsePositive);
                }}
                disabled={
                  !!getMarkedStatus ||
                  getIgnoreStatus === LARIgnoreStatus.ignored
                }
              />,
              <Actions.Item
                key="mark-as-by-design"
                title="Mark as By Design"
                icon="FeatherThumbsUp"
                onClick={() => {
                  setIsIgnoreDialogOpen(true);
                  setSelectedMarkAs(LarIgnoreReason.ByDesign);
                }}
                disabled={
                  !!getMarkedStatus ||
                  getIgnoreStatus === LARIgnoreStatus.ignored
                }
              />,
              <Actions.Item
                key="mark-as-dev-test-cluster"
                title="Mark as Dev/Test Cluster"
                icon="FeatherMonitor"
                onClick={() => {
                  setIsIgnoreDialogOpen(true);
                  setSelectedMarkAs(LarIgnoreReason.DevTestCluster);
                }}
                disabled={
                  !!getMarkedStatus ||
                  getIgnoreStatus === LARIgnoreStatus.ignored
                }
              />,
              <Actions.Item
                key="ignore"
                title="Ignore"
                icon="FeatherBellOff"
                onClick={() => {
                  setIsIgnoreDialogOpen(true);
                }}
                disabled={getIgnoreStatus === LARIgnoreStatus.ignored}
              />,

              <Actions.Item
                key="unignore"
                title="Unignore"
                icon="FeatherBellRing"
                onClick={() => {
                  setIsIgnoreDialogOpen(true);
                }}
                disabled={getIgnoreStatus !== LARIgnoreStatus.ignored}
              />,
              <Actions.Item
                key="unmark"
                title="Unmark"
                icon="FeatherLightbulbOff"
                onClick={() => {
                  setIsIgnoreDialogOpen(true);
                }}
                disabled={!getMarkedStatus}
              />,
            ],
          },
          {
            title: 'Resolve Risk',
            items: [
              <Actions.Item
                key="create-jira-ticket"
                title={
                  getJiraState && getJiraState.jiraId
                    ? `Jira ${getJiraState.jiraId}`
                    : 'Create Jira Ticket'
                }
                icon="FeatherTag"
                onClick={async () =>
                  !getJiraState?.jiraId && createJiraTicket()
                }
                link={getJiraState?.jiraId ? getJiraState.jiraLink : undefined}
                disabled={isLoadingLarStatus}
              />,
            ],
          },
        ]}
      />
    </div>
  );
};
